export const BRAINTREE_VARIANT_NAMES = ['email'] as const;

export type TpVariants = (typeof BRAINTREE_VARIANT_NAMES)[number];

export const STRIPE_VARIANT_NAMES = [
  'bonus',
  'cancellation',
  'default',
  'email2',
  'email3',
  'email4',
  'emailtraf',
  'google21',
  'googlemewing',
  'health',
  'stressless',
  'tiktok21',
  'variant21fall',
  'variant21',
  'short21',
  'short22',
  'short23',
  'short24',
  'short25',
  'short26',
  'variant27',
  'variant28',
  'variant29',
  'variant30',
  'variant31',
  'variant21reg',
  'tiktok21',
  'variant21agencyFB',
  'variant21agencyTikTok',
  'variant21agencyGoogle',
  'TW21',
  'PT21',
  'mewing',
  'mewingPT',
  'mewingPT_chp',
  'mewingwomen',
  'mewingGoogle',
  'mewingTW',
  'mewingAg',
  'mewing22',
  'mewingTikTok',
  'mewingTikTok_chp',
  'variant21pix',
  'variant21of',
  'variant21bundle',
  'variant21bundle_chp',
  'variant21bundlePT',
  'variant21bundlePT_chp',
  'variant21bundleTT',
  'variant21bundleTT_chp',
  'variant21_tr_m1',
  'mewing_tr_ch',
  'variant21_tr_m2',
  'variant21_tr_y1',
  'mewing_tr_m1',
  'mewing_tr_m2',
  'mewing_tr_y1',
  'asymmetry',
  'asymmetry_women',
  'maxi',
  'variant21SP',
  'MewingSP',
  'AsymmetryPT',
  'AsymmetryTT',
  'asymmetry_np',
  'maxiPT',
  'maxiTT',
] as const;

export type TpStripeVariants = (typeof STRIPE_VARIANT_NAMES)[number];

export type TpPaymentSystemRegion = 'WW';

export type TpSource =
  | 'facebook'
  | 'twitter'
  | 'google'
  | 'tiktok'
  | 'bing'
  | 'snapchat'
  | 'pinterest';
export type TpPaymentSystem = 'braintree' | 'paddle' | 'stripe';

export type TpVariantObject = {
  variant_name: TpVariants | TpStripeVariants;
  payment_system: TpPaymentSystem;
  source: TpSource;
  isApi: boolean;
  payment_system_region: TpPaymentSystemRegion;
};

const DEFAULT_PAYMENT_SYSTEM: TpPaymentSystem = 'stripe';

export type CreateVariantParams = {
  source: TpSource;
  names: readonly (TpVariants | TpStripeVariants)[];
  paymentSystemRegion?: TpPaymentSystemRegion;
  paymentSystem?: TpPaymentSystem;
  isApi: boolean;
};

const createVariant = ({
  source,
  names,
  isApi,
  paymentSystemRegion = 'WW',
  paymentSystem = DEFAULT_PAYMENT_SYSTEM,
}: CreateVariantParams): TpVariantObject[] =>
  names.map((variant_name) => ({
    variant_name,
    isApi,
    payment_system: paymentSystem,
    source,
    payment_system_region: paymentSystemRegion,
  }));

export type VariantConfig = {
  braintree: Record<TpSource, readonly (TpVariants | TpStripeVariants)[]>;
  stripe: Record<TpSource, readonly TpStripeVariants[]>;
};

export const VARIANT_CONFIG: VariantConfig = {
  braintree: {
    facebook: ['email'],
    twitter: [],
    pinterest: [],
    google: [],
    tiktok: [],
    bing: [],
    snapchat: [],
  },
  stripe: {
    facebook: [
      'variant21pix',
      'variant21of',
      'emailtraf',
      'variant21fall',
      'variant21',
      'variant21reg',
      'default',
      'email2',
      'email3',
      'email4',
      'bonus',
      'stressless',
      'cancellation',
      'health',
      'variant21agencyFB',
      'mewing',
      'mewingAg',
      'mewing22',
      'short21',
      'short22',
      'short23',
      'short24',
      'short25',
      'short26',
      'variant27',
      'variant28',
      'variant29',
      'variant30',
      'variant31',
      'variant21bundle',
      'mewingwomen',
      'variant21_tr_m1',
      'variant21_tr_m2',
      'variant21_tr_y1',
      'mewing_tr_m1',
      'mewing_tr_m2',
      'mewing_tr_y1',
      'asymmetry',
      'asymmetry_women',
      'maxi',
      'variant21bundle_chp',
      'asymmetry_np',
      'mewing_tr_ch',
    ],
    twitter: ['TW21', 'mewingTW'],
    pinterest: [
      'PT21',
      'mewingPT',
      'variant21bundlePT',
      'mewingPT_chp',
      'variant21bundlePT_chp',
      'AsymmetryPT',
      'maxiPT',
    ],
    google: [
      'google21',
      'googlemewing',
      'variant21agencyGoogle',
      'mewingGoogle',
    ],
    tiktok: [
      'tiktok21',
      'variant21agencyTikTok',
      'mewingTikTok',
      'variant21bundleTT',
      'mewingTikTok_chp',
      'variant21bundleTT_chp',
      'AsymmetryTT',
      'maxiTT',
    ],
    bing: [],
    snapchat: [
      'variant21',
      'mewing_tr_ch',
      'mewing_tr_m1',
      'asymmetry',
      'asymmetry_women',
      'mewing',
      'variant21bundle',
      'variant21bundle_chp',
      'variant21SP',
      'MewingSP',
    ],
  },
};

const createAllVariants = (): Array<TpVariantObject> => {
  const variants: TpVariantObject[] = [];

  for (const paymentSystem of Object.keys(
    VARIANT_CONFIG,
  ) as (keyof VariantConfig)[]) {
    const paymentSystemConfig = VARIANT_CONFIG[paymentSystem];

    for (const [source, variantNames] of Object.entries(paymentSystemConfig)) {
      if (
        (variantNames as readonly (TpVariants | TpStripeVariants)[]).length > 0
      ) {
        variants.push(
          ...createVariant({
            isApi: true,
            source: source as TpSource,
            names: variantNames as readonly (TpVariants | TpStripeVariants)[],
            paymentSystem: paymentSystem,
          }),
        );
      }
    }
  }

  return variants;
};

const createGroupedVariants = () => {
  const variants = createAllVariants();

  return {
    byPaymentSystem: Map.groupBy(variants, (variant) => variant.payment_system),
    bySource: Map.groupBy(variants, (variant) => variant.source),
    all: variants,
  };
};

export const GROUPED_VARIANTS = createGroupedVariants();
export const ALL_VARIANTS: Array<TpVariantObject> = GROUPED_VARIANTS.all;

export const EMAIL_VARIANTS = ALL_VARIANTS.filter((item) =>
  item.variant_name.toLowerCase().includes('email'),
).map((item) => item.variant_name);

export const MEWING_VARIANTS = ALL_VARIANTS.filter((item) =>
  item.variant_name.toLowerCase().includes('mewing'),
).map((item) => item.variant_name);

export const SHORT_VARIANTS = ALL_VARIANTS.filter((item) =>
  item.variant_name.includes('short'),
).map((item) => item.variant_name);

export const MAXI_VARIANTS = ALL_VARIANTS.filter((item) =>
  item.variant_name.includes('maxi'),
).map((item) => item.variant_name);
export const ASYM_VARIANTS = ALL_VARIANTS.filter((item) =>
  item.variant_name.toLowerCase().includes('asym'),
).map((item) => item.variant_name);

export const BUNDLE_VARIANTS = ALL_VARIANTS.filter((item) =>
  item.variant_name.includes('bundle'),
).map((item) => item.variant_name);
